'use client'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { authFetch } from '@/lib/auth/auth-fetch'
import { useWallet } from '@/providers/WalletContext'
import { useModalStore } from '@/stores/modal-store'
import { Check, Copy, ExternalLink, Sparkles } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'

type InviteResponse = {
	code: string
	maxUses: number
}

// Tuning for aggressive retry while waiting for auth / the API to settle.
const MAX_ATTEMPTS = 15
const INITIAL_DELAY_MS = 500
const MAX_DELAY_MS = 5000

export function JoinChanceAiModal() {
	const t = useTranslations('joinChanceAi')
	const { authenticated } = useWallet()
	const { activeModal, closeModal } = useModalStore()
	const isOpen = activeModal === 'join-chance-ai'

	const [invite, setInvite] = useState<InviteResponse | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [copied, setCopied] = useState(false)

	const attemptRef = useRef(0)
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const inFlightRef = useRef(false)

	const clearRetry = useCallback(() => {
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current)
			retryTimerRef.current = null
		}
	}, [])

	const tryFetch = useCallback(async () => {
		if (inFlightRef.current) return
		if (!authenticated) {
			// Auth not ready yet — schedule a retry; effect will also re-fire
			// when `authenticated` updates, whichever comes first.
			if (attemptRef.current >= MAX_ATTEMPTS) {
				setIsLoading(false)
				setError(t('notAuthenticated'))
				return
			}
			attemptRef.current += 1
			const delay = Math.min(INITIAL_DELAY_MS * 2 ** (attemptRef.current - 1), MAX_DELAY_MS)
			clearRetry()
			retryTimerRef.current = setTimeout(tryFetch, delay)
			return
		}

		inFlightRef.current = true
		setIsLoading(true)
		setError(null)

		try {
			const res = await authFetch('/api/chance-ai/invite', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({}),
			})
			const data = await res.json()
			if (!res.ok) throw new Error(data?.error || 'Failed to get invite')
			setInvite(data as InviteResponse)
			setIsLoading(false)
			attemptRef.current = 0
		} catch (err) {
			console.error('[JoinChanceAi] attempt failed:', err)
			if (attemptRef.current >= MAX_ATTEMPTS) {
				setIsLoading(false)
				setError(err instanceof Error ? err.message : t('error'))
			} else {
				attemptRef.current += 1
				const delay = Math.min(INITIAL_DELAY_MS * 2 ** (attemptRef.current - 1), MAX_DELAY_MS)
				clearRetry()
				retryTimerRef.current = setTimeout(tryFetch, delay)
			}
		} finally {
			inFlightRef.current = false
		}
	}, [authenticated, clearRetry, t])

	useEffect(() => {
		if (!isOpen) {
			clearRetry()
			attemptRef.current = 0
			setError(null)
			setCopied(false)
			setIsLoading(false)
			return
		}

		// User not logged in at all — no point retrying.
		if (!authenticated) {
			setError(t('notAuthenticated'))
			setIsLoading(false)
			return
		}

		// tryFetch is memoised on `authenticated`; effect re-runs when it updates.
		tryFetch()

		return () => {
			clearRetry()
		}
	}, [isOpen, authenticated, tryFetch, clearRetry, t])

	useEffect(() => {
		return () => {
			clearRetry()
		}
	}, [clearRetry])

	const inviteUrl = invite ? `https://ai.chance.cc/invite/${invite.code}` : ''

	const handleCopy = async () => {
		if (!invite) return
		try {
			await navigator.clipboard.writeText(invite.code)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch (err) {
			console.error('Failed to copy invite code:', err)
		}
	}

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && closeModal()}>
			<DialogContent className="max-w-md border-white/10 bg-brand-dark">
				<div className="flex flex-col items-center gap-4 text-center">
					<div className="flex h-16 w-16 items-center justify-center rounded-full bg-sky-500/15">
						<Sparkles className="h-8 w-8 text-sky-400" />
					</div>
					<DialogTitle className="text-white text-xl">{t('title')}</DialogTitle>
					<DialogDescription className="text-white/60">{t('description')}</DialogDescription>
				</div>

				<div className="mt-4 flex flex-col gap-4">
					{isLoading && !invite && (
						<div className="flex h-12 items-center justify-center rounded-lg border border-white/10 bg-white/5">
							<p className="text-sm text-white/60">{t('loading')}</p>
						</div>
					)}

					{error && !isLoading && !invite && (
						<p className="text-center text-red-400 text-sm">{error}</p>
					)}

					{invite && (
						<>
							<button
								type="button"
								onClick={handleCopy}
								className="group flex h-14 items-center justify-between rounded-lg border border-white/10 bg-white/5 px-4 transition-colors hover:bg-white/10"
							>
								<span className="font-mono text-lg text-white uppercase tracking-wider">
									{invite.code}
								</span>
								{copied ? (
									<Check className="size-5 text-brand-green" />
								) : (
									<Copy className="size-5 text-white/60 group-hover:text-white" />
								)}
							</button>

							<Button
								asChild
								className="h-12 bg-brand-blue font-semibold text-base text-white hover:bg-brand-blue/90"
							>
								<a href={inviteUrl} target="_blank" rel="noopener noreferrer">
									<ExternalLink className="size-[18px]" />
									{t('openLink')}
								</a>
							</Button>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
